// apps/web/src/pages/agent/reports/CampaignPerformanceReportPage.tsx
/**
 * Reports → Conversation Reports → Campaign Performance.
 *
 * Same toolbar/table/pagination structure as TeamPerformanceReportPage —
 * see that file for the full rationale on the layout choices reused here
 * (no client pagination beyond the bounded result set, filter drawer as the
 * empty state's call to action, toolbar classes lifted from ConversationInbox).
 */
import { useRef, useState, useEffect, type JSX } from 'react'
import {
  Columns3, Megaphone, GripVertical, Check, SlidersHorizontal, ChevronDown, Loader2,
  ChevronLeft, ChevronRight,
} from 'lucide-react'

import CampaignPerformanceFilters from '../../../components/reports/campaign-performance/CampaignPerformanceFilters.js'
import CampaignPerformanceTable from '../../../components/reports/campaign-performance/CampaignPerformanceTable.js'
import CampaignPerformanceAnalytics from '../../../components/reports/campaign-performance/CampaignPerformanceAnalytics.js'
import ReportViewTabs, { type ReportView } from '../../../components/reports/analytics/ReportViewTabs.js'
import {
  CAMPAIGN_PERFORMANCE_COLUMNS,
  CAMPAIGN_PERFORMANCE_REPORT_ID,
  DEFAULT_VISIBLE_COLUMNS,
  sanitizeColumnOrder,
} from '../../../components/reports/campaign-performance/columns.js'
import {
  useCampaignPerformanceReport,
  useCampaignPerformanceTimeline,
  type CampaignReportFilters,
} from '../../../hooks/useCampaignPerformanceReport.js'
import { useReportView } from '../../../hooks/useReportView.js'
import ReportExportMenu from '../../../components/reports/ReportExportMenu.js'
import Select from '../../../components/ui/Select.js'
import ReportSearch, { useReportSearch } from '../../../components/reports/ReportSearch.js'

const R = 'rounded-[3px]'
const CONTROL = `flex h-8 items-center gap-2 ${R} border border-slate-200 bg-white px-2.5 text-[12.5px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900`
const CONTROL_ON = 'border-[#C9D9FB] bg-blue-50 text-[#3366FF] hover:border-[#C9D9FB] hover:text-[#3366FF]'
const MENU = `absolute top-full z-40 mt-1.5 overflow-hidden ${R} border border-slate-200 bg-white py-1 shadow-xl shadow-black/5`

export default function CampaignPerformanceReportPage(): JSX.Element {
  const { rows, loading, error, hasRun, run, reset } = useCampaignPerformanceReport()
  const timeline = useCampaignPerformanceTimeline()
  const { columns, setColumns, setColumnsLocal, commit } = useReportView(
    CAMPAIGN_PERFORMANCE_REPORT_ID,
    DEFAULT_VISIBLE_COLUMNS,
    sanitizeColumnOrder,
  )

  const [filtersOpen, setFiltersOpen] = useState(true)
  const [columnMenuOpen, setColumnMenuOpen] = useState(false)
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [ranWith, setRanWith] = useState<CampaignReportFilters | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [search, setSearch] = useState('')
  // Table ⇄ Analytics over the same rows — switching never re-queries.
  const [view, setView] = useState<ReportView>('table')
  const columnMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!columnMenuOpen) return
    const onDown = (e: MouseEvent): void => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target as Node)) {
        setColumnMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [columnMenuOpen])

  const handleRun = (filters: CampaignReportFilters): void => {
    setRanWith(filters)
    setFiltersOpen(false)
    setPage(1)
    void run(filters)
    // The old timeline belongs to the previous filter set — dropped so a
    // stale heatmap can't linger behind newly-run aggregate rows.
    timeline.reset()
  }

  const handleReset = (): void => {
    setRanWith(null)
    reset()
    timeline.reset()
  }

  // Fetched lazily, once per report run — not on every table⇄analytics toggle.
  const handleViewChange = (next: ReportView): void => {
    setView(next)
    if (next === 'analytics' && ranWith && !timeline.hasFetched && !timeline.loading) {
      void timeline.fetch(ranWith)
    }
  }

  const toggleColumn = (key: string): void => {
    if (CAMPAIGN_PERFORMANCE_COLUMNS.find((c) => c.key === key)?.locked) return
    const next = columns.includes(key)
      ? columns.filter((k) => k !== key)
      : [...columns, key].sort(
          (a, b) => DEFAULT_VISIBLE_COLUMNS.indexOf(a) - DEFAULT_VISIBLE_COLUMNS.indexOf(b),
        )
    setColumns(next)
  }

  const reorder = (from: string, to: string): void => {
    if (from === to) return
    setColumnsLocal((prev) => {
      const next = [...prev]
      const fromIdx = next.indexOf(from)
      const toIdx = next.indexOf(to)
      if (fromIdx === -1 || toIdx === -1) return prev
      next.splice(toIdx, 0, ...next.splice(fromIdx, 1))
      return next
    })
  }

  const visible = useReportSearch(rows, search, columns, (r, k) => (r as unknown as Record<string, unknown>)[k])
  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize))
  const safePage = Math.min(page, totalPages)
  const pageRows = visible.slice((safePage - 1) * pageSize, safePage * pageSize)

  const hiddenColumns = CAMPAIGN_PERFORMANCE_COLUMNS.length - columns.length

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <header className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-slate-200 bg-white px-5 py-3">
        <div className="flex shrink-0 items-center gap-3">
          <span className={`flex h-10 w-10 items-center justify-center border border-blue-200 ${R} bg-blue-50 text-[#3366FF]`}>
            <Megaphone className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-lg font-bold leading-tight tracking-tight text-slate-900">
              Campaign Performance
            </h1>
            <p className="text-[12px] leading-tight text-slate-400">
              {ranWith
                ? `${rows.length} row${rows.length === 1 ? '' : 's'}`
                  + ` · ${ranWith.startDate} ${ranWith.startTime || '00:00'}`
                  + ` → ${ranWith.endDate} ${ranWith.endTime || '23:59'}`
                  + ` · ${ranWith.timezone}`
                  + ` · ${ranWith.interval || '1d'}`
                : 'Conversation volume and outcomes per campaign'}
            </p>
          </div>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
        <ReportViewTabs value={view} onChange={handleViewChange} disabled={!hasRun} />

        <ReportSearch
          value={search}
          onChange={(v) => { setSearch(v); setPage(1) }}
          disabled={!hasRun || rows.length === 0}
          placeholder="Search campaigns…"
        />

        <button
          onClick={() => setFiltersOpen(true)}
          className={`${CONTROL} ${ranWith ? CONTROL_ON : ''}`}
          title="Report filters"
        >
          <SlidersHorizontal className={`h-4 w-4 ${ranWith ? 'text-[#3366FF]' : 'text-slate-400'}`} />
          Filters
        </button>

        {/* Column choice belongs to the table; the analytics view has no columns. */}
        <div ref={columnMenuRef} className={`relative ${view === 'analytics' ? 'hidden' : ''}`}>
          <button
            onClick={() => setColumnMenuOpen((v) => !v)}
            className={`${CONTROL} ${hiddenColumns > 0 ? CONTROL_ON : ''}`}
            title="Choose columns"
          >
            <Columns3 className={`h-4 w-4 ${hiddenColumns > 0 ? 'text-[#3366FF]' : 'text-slate-400'}`} />
            Columns
            {hiddenColumns > 0 && (
              <span className="text-[11px] font-bold tabular-nums">
                {columns.length}/{CAMPAIGN_PERFORMANCE_COLUMNS.length}
              </span>
            )}
            <ChevronDown
              className={`h-3.5 w-3.5 text-slate-400 transition-transform ${columnMenuOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {columnMenuOpen && (
            <div className={`${MENU} right-0 max-h-[420px] w-64 overflow-y-auto`}>
              <p className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">
                Drag to reorder · saved to your profile
              </p>
              {CAMPAIGN_PERFORMANCE_COLUMNS.map((col) => {
                const checked = columns.includes(col.key)
                return (
                  <div
                    key={col.key}
                    draggable={!col.locked}
                    onDragStart={() => setDragKey(col.key)}
                    onDragOver={(e) => {
                      e.preventDefault()
                      if (dragKey && dragKey !== col.key) reorder(dragKey, col.key)
                    }}
                    onDragEnd={() => { setDragKey(null); commit() }}
                    onClick={() => toggleColumn(col.key)}
                    className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[12.5px] font-medium transition-colors hover:bg-slate-50 ${
                      dragKey === col.key ? 'opacity-50' : ''
                    }`}
                  >
                    <GripVertical
                      className={`h-3.5 w-3.5 shrink-0 ${col.locked ? 'text-slate-200' : 'text-slate-300'}`}
                    />
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center ${R} border ${
                        checked ? 'border-[#3366FF] bg-[#3366FF]' : 'border-slate-300 bg-white'
                      }`}
                    >
                      {checked && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <span className={`truncate ${col.locked ? 'text-slate-400' : 'text-slate-700'}`}>
                      {col.label}
                    </span>
                    {col.locked && (
                      <span className="ml-auto text-[10px] font-semibold uppercase text-slate-300">
                        Pinned
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

          <ReportExportMenu
            reportId={CAMPAIGN_PERFORMANCE_REPORT_ID}
            reportName="Campaign Performance"
            params={ranWith && {
              timezone: ranWith.timezone,
              startDate: ranWith.startDate,
              endDate: ranWith.endDate,
              startTime: ranWith.startTime,
              endTime: ranWith.endTime,
              campaignIds: ranWith.campaignIds.join(','),
            }}
            columns={columns}
            disabled={!hasRun || rows.length === 0}
            controlClass={CONTROL}
            menuClass={MENU}
          />
        </div>
      </header>

      {error && (
        <div role="alert" className="border-b border-red-100 bg-red-50 px-5 py-2.5 text-[12.5px] text-red-700">
          {error}
        </div>
      )}

      <div className="scrollbar-thin relative flex-1 overflow-auto bg-white">
        {loading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
            <Loader2 className="h-6 w-6 animate-spin text-[#3366FF]" />
          </div>
        )}

        {!hasRun && !loading ? (
          <EmptyState
            title="No report yet"
            description="Choose a start date, end date and campaign in Filters, then run the report."
            actionLabel="Open filters"
            onAction={() => setFiltersOpen(true)}
          />
        ) : hasRun && visible.length === 0 && !loading && !error ? (
          <EmptyState
            title={search ? 'No matching rows' : 'No activity in this window'}
            description={search
              ? 'No campaign matches your search. Clear it, or adjust the filters.'
              : 'No campaign in the selected scope had a conversation between those dates. Try widening the window, or selecting more campaigns.'}
            actionLabel="Change filters"
            onAction={() => setFiltersOpen(true)}
          />
        ) : view === 'analytics' ? (
          <CampaignPerformanceAnalytics
            rows={visible}
            timelineRows={timeline.rows}
            timelineLoading={timeline.loading}
            timelineTruncated={timeline.truncated}
          />
        ) : (
          <CampaignPerformanceTable rows={pageRows} visibleColumns={columns} />
        )}
      </div>

      {hasRun && visible.length > 0 && view === 'table' && (
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 bg-white px-5 py-2.5">
          <div className="flex items-center gap-2 text-[12px] text-slate-500">
            <span>Rows per page</span>
            <Select
              ariaLabel="Rows per page"
              size="sm"
              value={String(pageSize)}
              onChange={(v) => { setPageSize(Number(v)); setPage(1) }}
              options={[25, 50, 100, 200].map((n) => ({ value: String(n), label: String(n) }))}
              className="w-[72px]"
            />
            <span className="ml-2 font-mono tabular-nums">
              {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, visible.length)} of {visible.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((n) => Math.max(1, n - 1))}
              disabled={safePage <= 1}
              className={`flex h-8 w-8 items-center justify-center ${R} border border-slate-200 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40`}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 font-mono text-[12px] font-semibold tabular-nums text-slate-600">
              {safePage} / {totalPages}
            </span>
            <button
              onClick={() => setPage((n) => Math.min(totalPages, n + 1))}
              disabled={safePage >= totalPages}
              className={`flex h-8 w-8 items-center justify-center ${R} border border-slate-200 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40`}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <CampaignPerformanceFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onRun={handleRun}
        onReset={handleReset}
        running={loading}
      />
    </div>
  )
}

function EmptyState({
  title,
  description,
  actionLabel,
  onAction,
}: {
  title: string
  description: string
  actionLabel: string
  onAction: () => void
}): JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 py-16 text-center">
      <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-50">
        <Megaphone className="h-5 w-5 text-slate-300" aria-hidden="true" />
      </span>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="mx-auto mt-1 max-w-md text-xs text-slate-500">{description}</p>
      <button
        onClick={onAction}
        className={`mt-4 flex h-8 items-center gap-2 ${R} bg-[#3366FF] px-4 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#2952CC]`}
      >
        <SlidersHorizontal className="h-4 w-4" />
        {actionLabel}
      </button>
    </div>
  )
}
